import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Download, ChevronLeft, Loader2, AlertCircle,
  TrendingUp, TrendingDown, Minus, Users, FileText, BarChart3, 
  Clock
} from 'lucide-react';
import { api } from '../api';
import './DailyBriefing.css';

interface Issue {
  id: string;
  title: string;
  heat_score: number;
  decision_mode: string;
  trend: string;
  article_count_24h: number;
  status: string;
  updated_at: string;
}

interface NewsAnalysis {
  articles_count: number;
  avg_heat_score: number;
  coverage_trend: string;
  top_sources: Array<{
    name: string;
    count: number;
  }>;
  sentiment_breakdown: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

interface StakeholderUpdate {
  id: string;
  name: string;
  type: string;
  influence: string;
  interest: string;
  relationship_status: string;
  updated_at: string | null;
  notes_preview: string | null;
  recent_mentions?: number;
  changes?: string[];
  profile_image?: string;
  affiliation?: string;
  title?: string;
}

interface CheckmateRisk {
  score: number;
  level: string;
  act_issues_count: number;
  high_influence_opposed_count: number;
}

interface KeyMetrics {
  total_active_issues: number;
  act_mode_count: number;
  monitor_mode_count: number;
  watch_mode_count: number;
  avg_heat_score: number;
  high_influence_stakeholders: number;
  checkmate_risk?: CheckmateRisk;
}

interface DailyBriefing {
  id: string;
  briefing_date: string;
  generated_at: string;
  executive_summary: string;
  high_impact_issues: Issue[];
  news_analysis_24h: NewsAnalysis;
  stakeholder_updates_24h: StakeholderUpdate[];
  key_metrics: KeyMetrics;
  pdf_url: string;
  has_viewed: boolean;
  viewed_at: string | null;
}

const DailyBriefing: React.FC = () => {
  const [briefing, setBriefing] = useState<DailyBriefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLatestBriefing();
  }, []);

  const fetchLatestBriefing = async () => {
    try {
      const data = await api.getLatestDailyBriefing();
      if (data.success) {
        setBriefing(data.briefing);
        // Mark as viewed if not already
        if (!data.briefing.has_viewed) {
          markAsViewed(data.briefing.id);
        }
      } else {
        setError(data.message || 'Failed to load briefing');
      }
    } catch (err: any) {
      console.error('Error fetching briefing:', err);
      if (err.status === 404 || err.message.includes('404') || err.message.includes('not found')) {
        setError('No briefings available yet');
      } else {
        setError('Failed to load briefing');
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsViewed = async (briefingId: string) => {
    try {
      await api.markDailyBriefingViewed(briefingId, [
        'executive_summary', 
        'high_impact_issues', 
        'news_analysis_24h', 
        'stakeholder_updates_24h', 
        'key_metrics'
      ]);
    } catch (err) {
      console.error('Error marking briefing as viewed:', err);
    }
  };

  const handleDownloadPDF = async () => {
    if (!briefing) return;
    
    setDownloading(true);
    try {
      await api.downloadDailyBriefingPDF(briefing.id);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getHeatBadgeClass = (score: number) => {
    if (score >= 70) return 'heat-badge-act';
    if (score >= 40) return 'heat-badge-monitor';
    return 'heat-badge-watch';
  };

  const getHeatBadgeLabel = (score: number) => {
    if (score >= 70) return 'ACT';
    if (score >= 40) return 'MONITOR';
    return 'WATCH';
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'rising') return <TrendingUp size={16} className="trend-icon-up" />;
    if (trend === 'falling') return <TrendingDown size={16} className="trend-icon-down" />;
    return <Minus size={16} className="trend-icon-stable" />;
  };

  if (loading) {
    return (
      <div className="daily-briefing-page">
        <div className="loading-container">
          <Loader2 className="spinner" size={48} />
          <p>Loading your daily briefing...</p>
        </div>
      </div>
    );
  }

  if (error || !briefing) {
    return (
      <div className="daily-briefing-page">
        <div className="error-container">
          <AlertCircle size={48} className="error-icon" />
          <h2>No Briefing Available</h2>
          <p>{error || 'Your first briefing will be generated at 6:00 AM'}</p>
          <button onClick={() => navigate('/dashboard')} className="back-button">
            <ChevronLeft size={18} />
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-briefing-page">
      {/* Header */}
      <div className="briefing-header">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          <ChevronLeft size={18} />
          Back to Dashboard
        </button>

        <div className="header-content">
          <div className="header-title">
            <FileText size={28} />
            <h1>Daily Briefing</h1>
          </div>
          <div className="header-meta">
            <div className="meta-item">
              <Calendar size={16} />
              <span>{formatDate(briefing.briefing_date)}</span>
            </div>
            <div className="meta-item">
              <Clock size={16} />
              <span>Generated at {formatTime(briefing.generated_at)}</span>
            </div>
          </div>
        </div>

        <button 
          onClick={handleDownloadPDF} 
          className="download-button"
          disabled={downloading}
        >
          {downloading ? (
            <>
              <Loader2 className="spinner" size={18} />
              Downloading...
            </>
          ) : (
            <>
              <Download size={18} />
              Download PDF
            </>
          )}
        </button>
      </div>

      {/* Executive Summary */}
      <section className="briefing-section executive-section">
        <div className="section-header">
          <h2>Executive Summary</h2>
        </div>
        <div className="section-content">
          <p className="executive-text">{briefing.executive_summary}</p>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="briefing-section metrics-section">
        <div className="section-header">
          <BarChart3 size={20} />
          <h2>Key Metrics</h2>
        </div>
        
        {/* Checkmate Risk - Prominent Display */}
        {briefing.key_metrics.checkmate_risk && (
          <div className="checkmate-risk-card">
            <div className="checkmate-header">
              <h3>Checkmate Risk</h3>
            </div>
            <div className="checkmate-content">
              <div className={`checkmate-level level-${briefing.key_metrics.checkmate_risk.level.toLowerCase()}`}>
                {briefing.key_metrics.checkmate_risk.level}
              </div>
              <div className="checkmate-details">
                <div className="detail-item">
                  <span className="detail-label">ACT Issues:</span>
                  <span className="detail-value">{briefing.key_metrics.checkmate_risk.act_issues_count}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">High-Influence Opposed:</span>
                  <span className="detail-value">{briefing.key_metrics.checkmate_risk.high_influence_opposed_count}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-value">{briefing.key_metrics.total_active_issues}</div>
            <div className="metric-label">Total Active Issues</div>
          </div>
          <div className="metric-card highlight-act">
            <div className="metric-value">{briefing.key_metrics.act_mode_count}</div>
            <div className="metric-label">ACT Mode</div>
          </div>
          <div className="metric-card highlight-monitor">
            <div className="metric-value">{briefing.key_metrics.monitor_mode_count}</div>
            <div className="metric-label">MONITOR Mode</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{briefing.key_metrics.watch_mode_count}</div>
            <div className="metric-label">WATCH Mode</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{briefing.key_metrics.avg_heat_score.toFixed(1)}</div>
            <div className="metric-label">Avg HEAT Score</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{briefing.key_metrics.high_influence_stakeholders}</div>
            <div className="metric-label">High-Influence Stakeholders</div>
          </div>
        </div>
      </section>

      {/* High-Impact Issues */}
      <section className="briefing-section issues-section">
        <div className="section-header">
          <AlertCircle size={20} />
          <h2>High-Impact Issues (Last 24 Hours)</h2>
          <span className="section-count">{briefing.high_impact_issues.length}</span>
        </div>
        <div className="section-content">
          {briefing.high_impact_issues.length === 0 ? (
            <div className="empty-state">
              <p>No high-impact issues in the last 24 hours</p>
            </div>
          ) : (
            <div className="issues-list">
              {briefing.high_impact_issues.map((issue) => (
                <div key={issue.id} className="issue-card">
                  <div className="issue-header">
                    <h3 className="issue-title">{issue.title}</h3>
                    <div className="issue-badges">
                      <span className={`heat-badge ${getHeatBadgeClass(issue.heat_score)}`}>
                        {getHeatBadgeLabel(issue.heat_score)} {issue.heat_score.toFixed(1)}
                      </span>
                      {getTrendIcon(issue.trend)}
                    </div>
                  </div>
                  <div className="issue-meta">
                    <span className="meta-item">
                      <FileText size={14} />
                      {issue.article_count_24h} articles
                    </span>
                    <span className="meta-item">
                      Updated {new Date(issue.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* News Analysis */}
      <section className="briefing-section news-section">
        <div className="section-header">
          <FileText size={20} />
          <h2>24-Hour News Analysis</h2>
        </div>
        <div className="section-content">
          <div className="news-summary">
            <div className="news-stat">
              <div className="stat-value">{briefing.news_analysis_24h.articles_count}</div>
              <div className="stat-label">Total Articles</div>
            </div>
            <div className="news-stat">
              <div className="stat-value">{briefing.news_analysis_24h.avg_heat_score.toFixed(1)}</div>
              <div className="stat-label">Avg HEAT Score</div>
            </div>
            <div className="news-stat">
              <div className="stat-value trend-indicator">
                {getTrendIcon(briefing.news_analysis_24h.coverage_trend)}
                {briefing.news_analysis_24h.coverage_trend}
              </div>
              <div className="stat-label">Coverage Trend</div>
            </div>
          </div>

          {briefing.news_analysis_24h.top_sources && briefing.news_analysis_24h.top_sources.length > 0 && (
            <div className="top-sources">
              <h4>Top Sources</h4>
              <div className="sources-table">
                {briefing.news_analysis_24h.top_sources.map((source, index) => (
                  <div key={index} className="source-row">
                    <span className="source-name">{source.name}</span>
                    <span className="source-count">{source.count} {source.count === 1 ? 'article' : 'articles'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {briefing.news_analysis_24h.sentiment_breakdown && (
            <div className="sentiment-breakdown">
              <h4>Sentiment Breakdown</h4>
              <div className="sentiment-bars">
                <div className="sentiment-bar">
                  <span className="sentiment-label">Positive</span>
                  <div className="sentiment-progress">
                    <div 
                      className="sentiment-fill positive"
                      style={{ width: `${briefing.news_analysis_24h.sentiment_breakdown.positive}%` }}
                    ></div>
                  </div>
                  <span className="sentiment-value">{briefing.news_analysis_24h.sentiment_breakdown.positive}%</span>
                </div>
                <div className="sentiment-bar">
                  <span className="sentiment-label">Neutral</span>
                  <div className="sentiment-progress">
                    <div 
                      className="sentiment-fill neutral"
                      style={{ width: `${briefing.news_analysis_24h.sentiment_breakdown.neutral}%` }}
                    ></div>
                  </div>
                  <span className="sentiment-value">{briefing.news_analysis_24h.sentiment_breakdown.neutral}%</span>
                </div>
                <div className="sentiment-bar">
                  <span className="sentiment-label">Negative</span>
                  <div className="sentiment-progress">
                    <div 
                      className="sentiment-fill negative"
                      style={{ width: `${briefing.news_analysis_24h.sentiment_breakdown.negative}%` }}
                    ></div>
                  </div>
                  <span className="sentiment-value">{briefing.news_analysis_24h.sentiment_breakdown.negative}%</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Stakeholder Updates */}
      <section className="briefing-section stakeholders-section">
        <div className="section-header">
          <Users size={20} />
          <h2>Stakeholder Updates (Last 24 Hours)</h2>
          <span className="section-count">{briefing.stakeholder_updates_24h.length}</span>
        </div>
        <div className="section-content">
          {briefing.stakeholder_updates_24h.length === 0 ? (
            <div className="empty-state">
              <p>No stakeholder updates in the last 24 hours</p>
            </div>
          ) : (
            <div className="stakeholders-grid">
              {briefing.stakeholder_updates_24h.map((stakeholder) => (
                <div key={stakeholder.id} className="stakeholder-card">
                  <div className="stakeholder-header">
                    {stakeholder.profile_image ? (
                      <img 
                        src={stakeholder.profile_image} 
                        alt={stakeholder.name}
                        className="stakeholder-profile-image"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="stakeholder-profile-placeholder">
                        {stakeholder.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="stakeholder-name-section">
                      <h4 className="stakeholder-name">{stakeholder.name}</h4>
                      {stakeholder.affiliation && (
                        <p className="stakeholder-affiliation">{stakeholder.affiliation}</p>
                      )}
                      {stakeholder.title && (
                        <p className="stakeholder-title">{stakeholder.title}</p>
                      )}
                    </div>
                  </div>
                  <div className="stakeholder-details">
                    <div className="detail-row">
                      <span className="detail-label">Influence:</span>
                      <span className={`detail-badge badge-${stakeholder.influence}`}>{stakeholder.influence}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Interest:</span>
                      <span className={`detail-badge badge-${stakeholder.interest}`}>{stakeholder.interest}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Relationship:</span>
                      <span className={`detail-badge badge-${stakeholder.relationship_status}`}>{stakeholder.relationship_status}</span>
                    </div>
                    {stakeholder.recent_mentions > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">Recent Mentions:</span>
                        <span className="detail-value highlight">{stakeholder.recent_mentions}</span>
                      </div>
                    )}
                    {stakeholder.changes && stakeholder.changes.length > 0 && (
                      <div className="detail-row changes-row">
                        <span className="detail-label">What Changed:</span>
                        <div className="changes-list">
                          {stakeholder.changes.map((change, idx) => (
                            <div key={idx} className="change-item">{change}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DailyBriefing;
